import { useState } from 'react';
import { getCookieConsent, setCookieConsent, type CookieConsent } from './cookieConsent';
import { applyCookieConsent, captureEvent } from '@/posthog-analytics/posthog';
import { PRIVACY_POLICY_URL } from './legalLinks';

/**
 * Site-wide cookie consent banner. Shown until the visitor makes a choice, which is
 * stored in a base-domain cookie so it carries across tenant subdomains. Accept and
 * Reject are given equal visual weight, as consent guidance requires. Strictly
 * necessary cookies (session, CSRF) are exempt and are never gated by this.
 *
 * No analytics event fires before the user accepts: the opt-in itself is the first
 * thing PostHog records, so nothing is captured without consent.
 */
export function CookieConsentBanner() {
  const [visible, setVisible] = useState(() => getCookieConsent() === null);

  if (!visible) return null;

  const choose = (consent: CookieConsent) => {
    setCookieConsent(consent);
    applyCookieConsent(consent);
    if (consent === 'granted') {
      captureEvent('cookie_consent_granted');
    }
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-gray-200 bg-white px-4 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          We use cookies to keep you signed in and, with your consent, to understand how the
          product is used. You can accept or reject non-essential cookies. See our{' '}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-700"
          >
            Privacy Policy
          </a>
          .
        </p>
        <div className="flex shrink-0 gap-3">
          {/* Equal prominence: same size and the same filled treatment, so neither
              choice is visually nudged (GDPR / CNIL guidance on consent). */}
          <button
            type="button"
            onClick={() => choose('denied')}
            className="flex-1 rounded-xl bg-gray-200 px-5 py-2.5 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-300 sm:flex-none"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => choose('granted')}
            className="flex-1 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 sm:flex-none"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
